/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useState } from 'react';
import './DataTableCommonTheme.scss';
import { Badge, Button, Tooltip } from '@scuf/common';
import { IBreakdownBySitesProps } from './BreakdownSitesProps';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import { ApiContext } from '../../../context/ApiContext';
import { IMSEmaaSPortfolioSiteTableProps } from '../../DashboardKPIWidgets/EMaaSMobileSupervisorPortfolioDashboard/IPortfolioProps';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { Deviation, SiteName, Value } from '../Utils/Constants';
import {
  getNullConverted,
  getNullConvertedValueForEnergyCardTable
} from '../Utils/EnergyUsagesViewDetailsUtils';
import AccountWarning from '../../../images/AccountWarning.svg';

const BreakdownSites = (props: IBreakdownBySitesProps) => {
  const {
    meterData,
    columnHeaders,
    onShowMore,
    maxRecords,
    activeWidgetName
  } = props;
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(Value);
  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  const apiContext = useContext(ApiContext);
  const { onEnergyUsageSiteClick } = apiContext;

  const onSiteclick = (
    data: IMSEmaaSPortfolioSiteTableProps,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (data && data.SiteId) {
      if (onEnergyUsageSiteClick) {
        event.stopPropagation();
        onEnergyUsageSiteClick(data.SiteId);
      }
    }
  };

  const cellDataRenderer = (cellData: ICellData) => {
    const { field, rowData } = cellData;

    if (field === SiteName) {
      return (
        <div
          className="emaas-site-summary"
          onClick={(event) => {
            onSiteclick(rowData, event);
          }}
        >
          {rowData.Deviation < 0 ? <Badge color="red" empty /> : '  '}{' '}
          {rowData.SiteName}
          {!!rowData.Message && (
            <Tooltip
              element={
                <img className="img-account-warning" src={AccountWarning} />
              }
              event="hover"
              content={rowData.Message}
              position="top center"
              size="small"
            />
          )}
        </div>
      );
    }
    if (field === Value) {
      return (
        <span className="table-cell-common">
          <Tooltip
            element={
              <span className="eui-unit-display-value">
                {getNullConvertedValueForEnergyCardTable(rowData.Value) !== '--'
                  ? getNullConvertedValueForEnergyCardTable(rowData.Value) +
                    ' ' +
                    cellData.rowData.Unit
                  : '--'}
              </span>
            }
            event="hover"
            content={
              getNullConvertedValueForEnergyCardTable(rowData.Value) !== '--'
                ? getNullConvertedValueForEnergyCardTable(rowData.Value) +
                  ' ' +
                  cellData.rowData.Unit
                : '--'
            }
            position="top center"
            size="small"
          />
        </span>
      );
    }
    if (field === Deviation) {
      if (rowData.Deviation === null) {
        return <>--</>;
      }
      return (
        <span className="table-cell-common">
          <Tooltip
            element={
              <span
                className={`${
                  rowData.Deviation < 0
                    ? 'saving-percentage-negative-cardview'
                    : 'saving-percentage-positive-cardview'
                }`}
              >
                {`${Math.abs(rowData.Deviation)} ${
                  activeWidgetName === 'eui'
                    ? rowData.Unit
                    : rowData.deviationUnit
                }  (${Math.abs(rowData.DeviationPercentage)}%)`}
              </span>
            }
            event="hover"
            content={`${Math.abs(rowData.Deviation)} ${
              activeWidgetName === 'eui' ? rowData.Unit : rowData.deviationUnit
            }  (${Math.abs(rowData.DeviationPercentage)}%)`}
            position="top center"
            size="small"
          />
        </span>
      );
    }
    return <></>;
  };

  return (
    <div className="Breakdown-table-container portfolio-dashboard">
      <DataTable
        data={meterData}
        rows={maxRecords}
        className={
          (meterData.length > maxRecords && 'style-tableContent show-more') ||
          'style-tableContent'
        }
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
        }}
        sortField={sortFieldName}
        sortOrder={fieldSortOrder}
      >
        {columnHeaders.map((header) => (
          <DataTable.Column
            key={header.toString()}
            field={header.Field}
            header={header.HeaderValue}
            renderer={cellDataRenderer}
            sortable={header.sortable}
            align={header.TextAlignValue}
            className={header?.className ?? ''}
          />
        ))}
      </DataTable>
      {meterData.length > maxRecords && (
        <div className="show-more-section">
          <Button
            type="link"
            onClick={(event) => {
              event.stopPropagation();
              onShowMore();
            }}
          >
            {t(d.SHOWMORE)}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BreakdownSites;
